import {
  faDollarSign,
  faInfinity,
  faPlus,
  faRepeat,
} from '@fortawesome/pro-regular-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Tab, Tabs } from 'src/components/Tabs';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { ServiceCount } from 'src/models/package/_types';
import { createPackage } from 'src/models/package/package';
import { isStripeConnected } from 'src/models/platform/utils';
import { Identifier } from 'src/types/utilityTypes';
import * as Yup from 'yup';

import { FormTextField } from '../Forms/Fields/FormTextField';
import { FormLocationSelect } from '../Forms/Selects/LocationSelect';
import { PaymentPlanSelect } from '../Forms/Selects/PaymentPlanSelect';
import { ServiceSelect } from '../Forms/Selects/ServiceSelect';
import { FormTrainerSelect } from '../Forms/Selects/TrainerSelect';

interface PackageFormValues {
  name: string;
  description?: string;
  serviceCounts: Pick<ServiceCount, 'serviceIds' | 'count'>[];
  locationId?: Identifier;
  assignedTrainerId?: Identifier;
  oneTimePayment?: number;
  processorPlans?: string[];
}

interface CreatePackageDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (packageId: Identifier) => void;
}

const validationSchema = (t, tCommon) =>
  Yup.object()
    .shape({
      name: Yup.string().required(
        tCommon('form.required', { field: t('fields.name') })
      ),
      description: Yup.string(),
      serviceCounts: Yup.array()
        .of(
          Yup.object().shape({
            serviceIds: Yup.array()
              .of(Yup.string())
              .min(1, tCommon('form.atLeastOne', { item: 'service' })),
            count: Yup.number().when('unlimited', {
              is: false,
              then: (schema) =>
                schema
                  .min(1, tCommon('form.minimum', { min: 1 }))
                  .required(tCommon('form.required', { field: 'Count' })),
              otherwise: (schema) => schema.notRequired(),
            }),
            unlimited: Yup.boolean(),
          })
        )
        .min(1, tCommon('form.atLeastOne', { item: 'service' })),
      locationId: Yup.string(),
      assignedTrainerId: Yup.string(),
      oneTimePayment: Yup.number(),
      processorPlans: Yup.array().of(Yup.string()),
    })
    .test(
      'payment-required',
      t('validation.paymentRequired'),
      function (values) {
        return !!(
          values.oneTimePayment ||
          (values.processorPlans && values.processorPlans.length > 0)
        );
      }
    );

const defaultValues: PackageFormValues = {
  name: '',
  description: '',
  serviceCounts: [{ serviceIds: [], count: 1 }],
  oneTimePayment: undefined,
  processorPlans: undefined,
};

export const CreatePackageDialog: FC<CreatePackageDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { user, session } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'dialog.createPackage',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const queryClient = useQueryClient();
  const [paymentType, setPaymentType] = useState<'oneTime' | 'subscription'>(
    'oneTime'
  );

  const createPackageMutation = useMutation({
    mutationFn: createPackage,
    onSuccess: (data) => {
      // Invalidate cartSearch queries so new packages appear in search results
      queryClient.invalidateQueries({
        queryKey: ['cartSearch'],
      });
      onSuccess(data.id);
      onClose();
    },
  });

  const handleSubmit = async (
    values: PackageFormValues,
    helpers: FormikHelpers<PackageFormValues>
  ) => {
    await createPackageMutation.mutateAsync({
      name: values.name,
      description: values.description,
      serviceCounts: values.serviceCounts as ServiceCount[],
      locationId: values.locationId,
      assignedTrainerId: values.assignedTrainerId,
      oneTimePayment: values.oneTimePayment,
      processorPlans: values.processorPlans,
    });

    helpers.setSubmitting(false);
    helpers.resetForm({
      values: {
        ...defaultValues,
        serviceCounts: [{ serviceIds: [], count: 1 }],
      },
    });
  };

  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={validationSchema(t, tCommon)}
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        isValid,
        setFieldValue,
        submitForm,
        resetForm,
      }) => {
        // Clear the other payment type when switching tabs
        const handlePaymentTypeChange = (
          _: unknown,
          newValue: 'oneTime' | 'subscription'
        ) => {
          setPaymentType(newValue);
          if (newValue === 'oneTime') {
            setFieldValue('processorPlans', undefined);
          } else {
            setFieldValue('oneTimePayment', undefined);
          }
        };

        const reset = () => {
          resetForm({
            values: {
              ...defaultValues,
              serviceCounts: [{ serviceIds: [], count: 1 }],
            },
          });
        };

        return (
          <Dialog
            open={open}
            onClose={() => {
              reset();
              onClose();
            }}
            title={t('title')}
            maxWidth="md"
            fullWidth
            actions={[
              {
                label: tCommon('buttons.cancel'),
                onClick: () => {
                  reset();
                  onClose();
                },
              },
              {
                variant: 'prominent',
                label: tCommon('buttons.create'),
                onClick: submitForm,
                disabled: !isValid || isSubmitting,
              },
            ]}
          >
            <Form>
              <Stack spacing={2}>
                <FormTextField name="name" label={t('fields.name')} required />
                <FormTextField
                  name="description"
                  label={t('fields.description')}
                  multiline
                  rows={3}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormLocationSelect
                      name="locationId"
                      label={t('fields.location')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormTrainerSelect
                      name="assignedTrainerId"
                      label={t('fields.trainer')}
                    />
                  </Grid>
                </Grid>

                <Card variant="outlined" sx={{ p: 2, my: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('fields.serviceGroupings')}
                  </Typography>
                  {values.serviceCounts.map((serviceCount, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 3,
                        pt: 2,
                        borderTop: index > 0 ? 1 : 0,
                        borderColor: 'divider',
                        position: 'relative',
                      }}
                    >
                      {index > 0 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newServiceCounts = [...values.serviceCounts];
                            newServiceCounts.splice(index, 1);
                            setFieldValue('serviceCounts', newServiceCounts);
                          }}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 8,
                            zIndex: (theme) => theme.zIndex.tooltip,
                          }}
                        >
                          <Icon size="small" icon={faTrash} />
                        </IconButton>
                      )}

                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <FormTextField
                              name={`serviceCounts.${index}.count`}
                              label={tCommon('labels.visits')}
                              type="number"
                              required
                              disabled={serviceCount.count === 0}
                              placeholder={
                                serviceCount.count === 0
                                  ? tCommon('labels.unlimited')
                                  : undefined
                              }
                              InputProps={{
                                inputProps: { min: 1 },
                              }}
                            />

                            <Tooltip
                              title={
                                serviceCount.count === 0
                                  ? tCommon('labels.limited')
                                  : tCommon('labels.unlimited')
                              }
                            >
                              <IconButton
                                sx={{ mt: 3 }}
                                onClick={() => {
                                  const newServiceCounts = [
                                    ...values.serviceCounts,
                                  ];
                                  newServiceCounts[index].count =
                                    newServiceCounts[index].count === 0 ? 1 : 0;
                                  setFieldValue(
                                    'serviceCounts',
                                    newServiceCounts
                                  );
                                }}
                                color={
                                  serviceCount.count === 0
                                    ? 'primary'
                                    : undefined
                                }
                              >
                                <Icon size="small" icon={faInfinity} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <ServiceSelect
                            value={serviceCount.serviceIds}
                            onChange={(newValue) => {
                              const newServiceCounts = [
                                ...values.serviceCounts,
                              ];
                              newServiceCounts[index].serviceIds =
                                Array.isArray(newValue) ? newValue : [newValue];
                              setFieldValue('serviceCounts', newServiceCounts);
                            }}
                            label={t('fields.services')}
                            multiple
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <Button
                    variant="borderless"
                    onClick={() => {
                      setFieldValue('serviceCounts', [
                        ...values.serviceCounts,
                        { serviceIds: [], count: 1 },
                      ]);
                    }}
                    startIcon={<Icon icon={faPlus} />}
                  >
                    {t('buttons.addServiceGrouping')}
                  </Button>
                </Card>

                <Card variant="outlined" sx={{ p: 2, my: 0 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('fields.paymentDetails')}
                  </Typography>
                  <Tabs
                    value={paymentType}
                    onChange={handlePaymentTypeChange}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                    <Tab
                      value="oneTime"
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon icon={faDollarSign} />
                          <span>{t('fields.oneTimePrice')}</span>
                        </Stack>
                      }
                    />
                    <Tab
                      value="subscription"
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Icon icon={faRepeat} />
                          <span>{t('fields.subscriptionPlan')}</span>
                        </Stack>
                      }
                    />
                  </Tabs>

                  {paymentType === 'oneTime' ? (
                    <Stack spacing={2}>
                      <FormTextField
                        name="oneTimePayment"
                        label={t('fields.oneTimePrice')}
                        type="number"
                        InputProps={{
                          startAdornment: '$',
                          inputProps: { min: 0.01 },
                        }}
                      />
                    </Stack>
                  ) : (
                    <PaymentPlanSelect
                      value={values.processorPlans || []}
                      onChange={(plans) => {
                        if (plans) {
                          setFieldValue(
                            'processorPlans',
                            Array.isArray(plans)
                              ? plans.map((p) => p.id)
                              : [plans.id]
                          );
                        }
                      }}
                      onCreatePlan={async (plan) => {
                        // Automatically add the newly created plan to the selected plans
                        const currentPlans = values.processorPlans || [];
                        setFieldValue('processorPlans', [
                          ...currentPlans,
                          plan.id,
                        ]);
                      }}
                      label={t('fields.subscriptionPlan')}
                      userId={user?.id}
                      multiple
                      hideCreateButton={!isStripeConnected(session)}
                    />
                  )}
                </Card>
              </Stack>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};
